/**
 * Staff Manager types — mirrors the steward staff profile API as proxied by
 * argus-backend (`/api/notes/staff/*`). All timestamps are unix **seconds**.
 */

export type StaffConfidence = 'none' | 'low' | 'medium' | 'high';

/**
 * Rating for a behavioural signal. `strong` sits above `solid` and is rare, so the UI
 * gives it a distinct highlighted treatment. Unknown strings are tolerated (rendered neutrally).
 */
export type StaffRating = 'strong' | 'solid' | 'mixed' | 'weak' | 'unknown';

export type CitationFacet =
  | 'communication'
  | 'expertise'
  | 'follow_through'
  | 'handoff'
  | 'ownership'
  | 'responsiveness';

export type CitationValence = 'good' | 'neutral' | 'poor';

/** Empty string means the observation is not tied to a ticket outcome. */
export type TicketHealth = '' | 'healthy' | 'slow' | 'stalled' | 'abandoned';

export interface StaffRosterEntry {
  discord_id: string;
  /** Discord username (lowercase handle). */
  username: string;
  display_name: string;
  name: string;
  has_profile: boolean;
  confidence: StaffConfidence | string;
  evidence_count: number;
  built_at: number | null;
  built_ago_seconds: number | null;
  window_days: number;
}

export interface StaffRosterResponse {
  count: number;
  profiled_count: number;
  generated_at: number;
  staff: StaffRosterEntry[];
}

export interface StaffCitation {
  id: number;
  facet: CitationFacet | string;
  valence: CitationValence;
  fact: string;
  quote: string;
  observed_at: number;
  seen_count: number;
  ticket_id: string;
  ticket_health: TicketHealth | string;
}

export interface StaffClaim {
  claim: string;
  evidence_ids: number[];
}

export interface StaffSignal {
  rating: StaffRating | string;
  note: string;
  sample_size: number;
  evidence_ids: number[];
}

export interface StaffEvidenceSummary {
  observations: number;
  tickets: number;
  healthy_tickets: number;
  stalled_tickets: number;
  oldest_at: number;
  newest_at: number;
}

export interface StaffSchedule {
  known: boolean;
  samples: number;
  days: number;
  active_hours_utc: number[];
  messages_by_hour_utc: number[];
  peak_hour_utc: number | null;
  start_hour_utc: number | null;
  end_hour_utc: number | null;
  share_in_window: number;
  weekend_share: number;
  caveat: string;
}

export interface StaffProfileDetails {
  summary: string;
  communication_style: string;
  current_load: string;
  activity_pattern: string;
  confidence: StaffConfidence | string;
  evidence_window_days: number;
  strengths: StaffClaim[];
  good_at: StaffClaim[];
  responsibilities: StaffClaim[];
  collaboration: StaffClaim[];
  watch_items: StaffClaim[];
  weaknesses: StaffClaim[];
  route_to_them_when: string[];
  unknowns: string[];
  prefers: string[];
  avoids: string[];
  do_not_route_when: string[];
  responsiveness: StaffSignal;
  follow_through: StaffSignal;
  reliability: StaffSignal;
  thoroughness: StaffSignal;
  does_their_best: StaffSignal;
}

export interface StaffProfileResponse {
  discord_id: string;
  username: string;
  display_name: string;
  name: string;
  is_staff: boolean;
  has_profile: boolean;
  built_at: number | null;
  built_ago_seconds: number | null;
  generated_at: number;
  window_days: number;
  evidence: StaffEvidenceSummary;
  schedule: StaffSchedule;
  citations: Record<string, StaffCitation>;
  profile: StaffProfileDetails | null;
}

export type StaffSignalKey =
  | 'responsiveness'
  | 'follow_through'
  | 'reliability'
  | 'thoroughness'
  | 'does_their_best';

export const STAFF_SIGNAL_KEYS: { key: StaffSignalKey; label: string }[] = [
  { key: 'responsiveness', label: 'Responsiveness' },
  { key: 'follow_through', label: 'Follow-through' },
  { key: 'reliability', label: 'Reliability' },
  { key: 'thoroughness', label: 'Thoroughness' },
  { key: 'does_their_best', label: 'Effort' },
];

export const CITATION_FACETS: CitationFacet[] = [
  'communication',
  'expertise',
  'follow_through',
  'handoff',
  'ownership',
  'responsiveness',
];

export const CITATION_VALENCES: CitationValence[] = ['good', 'neutral', 'poor'];

// ---------------------------------------------------------------------------------------------
// Ticket counts, monthly MVP and raffle (`/api/notes/staff/ticket-counts`)
// ---------------------------------------------------------------------------------------------

/** Roles that count tickets. `null` on a person means they appear in history but hold none now. */
export type StaffTicketRole = 'administrator' | 'moderator' | 'helper' | 'bot';

/** Why a person got no raffle entries in a finalized month. */
export type RaffleExclusion =
  | 'mvp'
  | 'below_minimum'
  | 'opted_out'
  | 'not_in_guild'
  | 'trainee'
  | 'not_eligible_role'
  | 'no_entries';

export interface StaffTicketMonth {
  /** `YYYY-MM`. */
  month: string;
  /** Whether the monthly raffle has processed this month. Always false for the current, open month. */
  finalized: boolean;
  /** `staff_points`, or `seed_file` for a manually corrected month; null while not finalized. */
  source: 'staff_points' | 'seed_file' | string | null;
  mvp_discord_id: string | null;
  mvp_tickets: number | null;
  raffle_winner_id: string | null;
  raffle_winner_entries: number | null;
  entrants_count: number | null;
  entries_total: number | null;
}

export interface StaffTicketStanding {
  tickets: number;
  /** false = the record is gone (bot downtime, lost export): treat `tickets` as unknown, not zero. */
  known: boolean;
  finalized: boolean;
  /** Raffle entries held that month; null while the month is open or unknown. */
  entries: number | null;
  /** Why they got 0 entries; null when they held entries or the month is not known. */
  excluded: RaffleExclusion | string | null;
  nominated: boolean;
  is_mvp: boolean;
  is_raffle_winner: boolean;
}

export interface StaffTicketPerson {
  discord_id: string;
  name: string;
  username: string;
  role: StaffTicketRole | string | null;
  /** Currently holds the admin / moderator / helper role. */
  is_staff: boolean;
  /** Sum over months where `known` is true. */
  total_known_tickets: number;
  /** Keyed by `YYYY-MM`. A month can be absent as well as present with `known: false`. */
  months: Record<string, StaffTicketStanding>;
}

export interface StaffTicketCountsResponse {
  generated_at: number;
  /** Oldest first, current (open) month last. */
  months: StaffTicketMonth[];
  staff: StaffTicketPerson[];
}
